import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import api from '@/utils/api';
import { 
  Shield, 
  FileText, 
  Clock, 
  UserCheck, 
  Lock,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

export default function PDPAConsentModal({ open, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [acknowledgeRetention, setAcknowledgeRetention] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPolicy();
    }
  }, [open]);

  const fetchPolicy = async () => {
    try {
      const response = await api.get('/pdpa/policy');
      setPolicy(response.data);
    } catch (error) {
      console.error('Failed to fetch PDPA policy:', error);
    }
  };

  const handleSubmit = async () => {
    if (!consentGiven || !acknowledgeRetention) {
      toast.error('Please accept the required consents to continue');
      return;
    }

    setLoading(true);
    try {
      await api.post('/pdpa/consent', {
        consent_given: consentGiven,
        marketing_consent: marketingConsent
      });
      
      toast.success('PDPA consent recorded successfully');
      onComplete?.();
    } catch (error) {
      toast.error('Failed to submit consent');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = consentGiven && acknowledgeRetention;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden [&>button.absolute]:hidden" data-testid="pdpa-consent-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Personal Data Protection</DialogTitle>
              <DialogDescription>
                PDPA Compliance - Singapore Standards
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-4">
          <div className="space-y-6">
            {/* Policy Version */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">Policy Version</span>
              </div>
              <Badge variant="secondary">{policy?.version || '1.0'}</Badge>
            </div>

            {/* Purpose */}
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-blue-500" />
                Purpose of Data Collection
              </h4>
              <p className="text-sm text-muted-foreground">
                {policy?.summary?.purpose || 'SAFER-I™ collects personal data to provide safety management services and improve workplace safety outcomes.'}
              </p>
            </div>

            {/* Data Collected */}
            <div>
              <h4 className="font-medium mb-2">Data We Collect</h4>
              <ul className="space-y-1">
                {(policy?.summary?.data_collected || [
                  'Name and contact information',
                  'Employment details and role',
                  'Safety check responses (anonymized for analytics)',
                  'Digital signatures for acknowledgments'
                ]).map((item, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Data Usage */}
            <div>
              <h4 className="font-medium mb-2">How We Use Your Data</h4>
              <ul className="space-y-1">
                {(policy?.summary?.data_usage || [
                  'Providing safety management services',
                  'Generating safety analytics and reports',
                  'Compliance with workplace safety regulations',
                  'Improving platform features'
                ]).map((item, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Data Retention */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-amber-800">
                <Clock className="w-4 h-4" />
                Data Retention (ISO 45001)
              </h4>
              <p className="text-sm text-amber-700">
                {policy?.summary?.data_retention || 'Personal data is retained for 7 years as per ISO 45001 requirements, then anonymized.'}
              </p>
            </div>

            {/* Your Rights */}
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4 text-purple-500" />
                Your Rights Under PDPA
              </h4>
              <ul className="space-y-1">
                {(policy?.summary?.user_rights || [
                  'Right to access your personal data',
                  'Right to correct inaccurate data',
                  'Right to withdraw consent',
                  'Right to request data deletion (subject to legal retention requirements)'
                ]).map((item, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <Separator />

            {/* Consent Checkboxes */}
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <Checkbox 
                  id="consent" 
                  checked={consentGiven}
                  onCheckedChange={setConsentGiven}
                  className="mt-0.5"
                  data-testid="pdpa-consent-checkbox"
                />
                <div className="space-y-1">
                  <Label htmlFor="consent" className="font-medium cursor-pointer">
                    I consent to the collection and use of my personal data *
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Required to use SAFER-I™ platform
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <Checkbox 
                  id="retention" 
                  checked={acknowledgeRetention}
                  onCheckedChange={setAcknowledgeRetention}
                  className="mt-0.5"
                  data-testid="retention-acknowledge-checkbox"
                />
                <div className="space-y-1">
                  <Label htmlFor="retention" className="font-medium cursor-pointer">
                    I acknowledge the 7-year data retention policy *
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Required for ISO 45001 compliance
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 border border-dashed rounded-lg hover:bg-muted/50 transition-colors">
                <Checkbox 
                  id="marketing" 
                  checked={marketingConsent}
                  onCheckedChange={setMarketingConsent}
                  className="mt-0.5"
                  data-testid="marketing-consent-checkbox"
                />
                <div className="space-y-1">
                  <Label htmlFor="marketing" className="font-medium cursor-pointer">
                    I agree to receive product updates and safety tips (optional)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    You can unsubscribe at any time
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mr-auto">
            <AlertCircle className="w-4 h-4" />
            <span>You can manage your data preferences anytime in Profile settings</span>
          </div>
          <Button 
            onClick={handleSubmit} 
            disabled={!canSubmit || loading}
            className="min-w-[150px]"
            data-testid="pdpa-submit-btn"
          >
            {loading ? 'Submitting...' : 'Accept & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
